// src/components/GildarcLearnLanding.js

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../Header/Header";
import MovingTicker from "../Header/MovingTicker";
import { useTranslation } from "react-i18next";
import Section1 from "../Page1Main/Sections/Section1";

const MODULES = [
  { id: "A", title: "mod-A-title", stories: 4, summary: "mod-A-summary" },
  {
    id: "B",
    title: "mod-B-title",
    stories: 11,
    summary: "mod-B-summary",
    liveNowLabel: "mod-B-liveNowLabel",
    storiesList: [
      { id: "B1", title: "mod-B1-title", path: "/saver", status: "available" },
],
  },
  { id: "C", title: "mod-C-title", stories: 8, summary: "mod-C-summary" },
  { id: "D", title: "mod-D-title", stories: 8, summary: "mod-D-summary" },
  { id: "E", title: "mod-E-title", stories: 11, summary: "mod-E-summary" },
  { id: "F", title: "mod-F-title", stories: 3, summary: "mod-F-summary" },
  { id: "G", title: "mod-G-title", stories: 8, summary: "mod-G-summary" },
  { id: "H", title: "mod-H-title", stories: 7, summary: "mod-H-summary" },
  { id: "I", title: "mod-I-title", stories: 8, summary: "mod-I-summary" },
  { id: "J", title: "mod-J-title", stories: 5, summary: "mod-J-summary" },
  { id: "K", title: "mod-K-title", stories: 11, summary: "mod-K-summary" },
  { id: "L", title: "mod-L-title", stories: 5, summary: "mod-L-summary" },
];

function AccordionItem({ module, isOpen, onToggle }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
<button
  type="button"
  onClick={onToggle}
  className={`
    flex w-full items-center justify-between px-4 sm:px-6 py-4 text-left transition
    ${module.id === "B"
      ? "bg-amber-300 hover:bg-amber-400"
      : "hover:bg-slate-50"}
  `}
>

        <div className="flex items-center gap-4 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-sm font-semibold text-slate-900">
            {module.id}
          </div>

          <div className="min-w-0">
          <div
  className={`
    font-semibold text-sm sm:text-base leading-snug
    ${module.id === "B"
      ? "text-emerald-700"
      : "text-slate-900"}
  `}
>
  {t(module.title)}
</div>


            <div className="text-xs text-slate-500 sm:text-sm flex items-center gap-2 mt-0.5">
              {module.stories} {t("mod-stories-label", "stories")}
              {module.liveNowLabel && (
                <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap">
                  {t(module.liveNowLabel)}
                </span>
              )}
            </div>
          </div>
        </div>

        <ChevronDown
          className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 sm:px-6 py-4 text-sm text-slate-700">
          <div className="font-semibold text-slate-800">{t(module.summary)}</div>

          {module.storiesList && (
            <ul className="mt-4 space-y-2">
              {module.storiesList.map((story) => (
                <li key={story.id}>
                  {story.status === "coming-soon" ? (
                    <div className="flex items-center justify-between rounded-xl px-3 py-3 text-xs sm:text-sm border bg-white border-slate-200 text-slate-500 opacity-80">
                      <span className="min-w-0">
                        <span className="font-semibold mr-2">{story.id}.</span>
                        {t(story.title)}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-400 whitespace-nowrap">
                        Coming soon
                      </span>
                    </div>
                  ) : (
                    <Link
                      to={story.path}
                      className="flex items-center justify-between rounded-xl px-3 py-3 text-xs sm:text-sm border bg-emerald-50 border-emerald-300 text-emerald-900 hover:bg-emerald-100 transition"
                    >
                      <span className="min-w-0">
                        <span className="font-semibold mr-2">{story.id}.</span>
                        {t(story.title)}
                      </span>
                      <span className="text-slate-600 text-sm whitespace-nowrap">
                        Start →
                      </span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ComingNextRow({ module }) {
  const { t } = useTranslation();

  return (
    <div
      className="
        grid grid-cols-[48px_1fr_auto] grid-rows-2 gap-x-4
        px-4 sm:px-6 py-4
        bg-white
        opacity-80 pointer-events-none
      "
    >
      {/* Letter (spans both rows) */}
      <div className="row-span-2">
        <div className="h-9 w-9 rounded-xl bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-600 leading-none mt-[2px]">
          {module.id}
        </div>
      </div>

      {/* Title (row 1) */}
      <div className="min-w-0 self-start">
        <div className="text-sm sm:text-base font-semibold text-slate-700 leading-snug text-left line-clamp-2">
          {t(module.title)}
        </div>
      </div>

      {/* Status (row 1) */}
      <div className="self-start text-xs font-semibold text-slate-400 whitespace-nowrap mt-[2px]">
        Coming soon
      </div>

      {/* Stories (row 2 aligned under title) */}
      <div className="col-start-2 row-start-2 text-xs text-slate-500 leading-tight text-left">
        {module.stories} {t("mod-stories-label", "stories")}
      </div>
    </div>
  );
}

export default function GildarcLearnLanding() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState("B");

  const liveModule = MODULES.find((m) => m.id === "B");
  const comingModules = MODULES.filter((m) => m.id !== "B");

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-100 via-orange-50 to-slate-50 text-slate-900">
      <div className="ticker-fixed">
        <MovingTicker>
          <>
            Millions of taxfilers miss out on benefits simply because we didn’t
            have guidance like Gildarc. This isn’t the usual tax education — for
            that you can visit
            <a
              href="https://apps.irs.gov/app/understandingTaxes/teacher/hows.jsp"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0f62fe",
                textDecoration: "underline",
                marginLeft: "6px",
              }}
            >
              the IRS Understanding Taxes site
            </a>
            . This explains the stages professionals usually charge money to
            walk people through.
          </>
        </MovingTicker>
      </div>

      <Header />

      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-10 sm:px-6 lg:py-14">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-white/80 p-5 sm:p-8 shadow-lg border border-orange-200"
        >
          <div className="text-center max-w-[860px] mx-auto">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900 border border-amber-300/30">
              <Sparkles className="h-4 w-4" />
              <span>Welcome to your GILDARC learning path</span>
            </div>

            <div className="flex justify-center items-center mb-10">
              <Section1 />
            </div>

            <div className="mt-4 text-left bg-amber-50/60 border border-amber-300/20 rounded-2xl p-4">
              <div className="text-[13px] font-extrabold text-slate-900 mb-1">
                {t("mod-noteS")}
              </div>

              <p className="m-0 text-[13px] leading-relaxed text-slate-500">
                You earn income. Not all of it is taxed. Some deductions reduce
                income first — this brings you to Adjusted Gross Income (AGI).
                More deductions are applied after AGI, which leads to taxable
                income. Tax is calculated on that amount. Non-refundable credits
                are applied next, and finally refundable credits and payments
                are compared to determine whether tax is owed or a refund
                results.
              </p>

              <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                Keeping this flow in mind makes it easier to understand where
                tax benefits can appear and why they apply.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Start here */}
        <section className="space-y-3">
          <div className="flex items-end justify-between">
            <h2 className="text-base sm:text-lg font-extrabold text-slate-900">
              Start here
            </h2>
            <span className="text-xs text-slate-500">One module, one story is live</span>
          </div>

          {liveModule && (
            <AccordionItem
              module={liveModule}
              isOpen={openId === "B"}
              onToggle={() => setOpenId((prev) => (prev === "B" ? "" : "B"))}
            />
          )}
        </section>

        {/* Coming next (organized list) */}
<section className="mt-6">
  <div className="flex items-end justify-between mb-3">
    <h2 className="text-base sm:text-lg font-extrabold text-slate-900">
      Coming next
    </h2>
    <span className="text-xs text-slate-500">
      More modules will appear here as they launch
    </span>
  </div>

  <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
    {comingModules.map((module, idx) => (
      <div
        key={module.id}
        className={`px-4 sm:px-6 py-4 bg-slate-50/80 opacity-80 pointer-events-none
          ${idx !== comingModules.length - 1 ? "border-b border-slate-100" : ""}
        `}
      >
        {/* 2-row grid: title row + stories row */}
        <div className="grid grid-cols-[48px_1fr_auto] gap-x-4">
          {/* Letter */}
          <div className="row-span-2">
            <div className="h-9 w-9 rounded-xl bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-600 leading-none mt-[2px]">
              {module.id}
            </div>
          </div>

          {/* Title + clamp (manual) */}
          <div className="min-w-0">
            <div
              className="text-sm sm:text-base font-semibold text-slate-700 leading-snug text-left overflow-hidden"
              style={{
                maxHeight: "2.6em",     // ~2 lines
                display: "block",
              }}
            >
              {t(module.title)}
            </div>
          </div>

          {/* Status */}
          <div className="text-xs font-semibold text-slate-400 whitespace-nowrap mt-[2px]">
            Coming 2026
          </div>

          {/* Stories aligned under title */}
          <div className="col-start-2 mt-1 text-xs text-slate-500 leading-tight text-left">
            {module.stories} {t("mod-stories-label", "stories")}
          </div>
        </div>
      </div>
    ))}
  </div>
</section>


      </div>

      <footer className="site-footer">
        <div className="footer-disclaimer">
        
        </div>
      </footer>
                         <Link to="/legal" className="legal-link">
        Terms of Use
      </Link>
    </div>
  );
}
